// Workspace / projects / channels CRUD RPC facades (knowledge/87 Phase 3).
// Extracted verbatim from app.svelte.ts. Mostly pure async client.* facades that
// mutate the shared workspaceState/channelState proxies, PLUS ProjectsWarmCache
// which holds module-level $state — so this MUST be a .svelte.ts (a rune in a
// plain .ts silently loses reactivity). app.svelte.ts re-exports every symbol
// below so importers (ChannelSidebar, tasks board, project/channel dialogs)
// never change.
//
// NOTE: leaveChannel stays in app.svelte.ts — it calls selectChannelPreview (the
// navigation spine, which must not move per knowledge/87 §4), so moving it here
// would cycle back through the app.svelte.ts barrel.
import { client } from "./client.js";
import { rememberChannelSlackGuests } from "../channel-slack-guests.js";
import {
  rememberChannelSeatedHumans,
  setChannelSeatedHumansLoader,
} from "../channel-seated-humans.svelte.js";
import {
  workspaceState,
  channelState,
  authState,
  notificationState,
  unreadFlagState,
} from "../core/state.svelte.js";
import type { Channel, Workspace } from "../core/types.js";
import type { AutonomyLevel } from "../core/autonomy.js";

// ─── Project actions ─────────────────────────────────────────────

export async function createWorkspace(
  name: string,
  opts: { idempotencyKey?: string } = {},
): Promise<Workspace> {
  const ws = await client.createWorkspace(name, undefined, opts.idempotencyKey);
  // The create_workspace relay response omits `role` (it only echoes id/name/
  // ownerId/avatarUrl/settings/createdAt — see relay-standalone.ts's
  // create_workspace_response), but the creator is ALWAYS the owner of a
  // workspace they just made (the relay does addMember(…, "owner")). Build the
  // owner-stamped Workspace once, insert it into the list AND return it: callers
  // (onboarding, workspace/+page) pass this exact object to selectWorkspace,
  // which sets workspaceState.current. Returning the raw roleless response would
  // leave current.role undefined → the `?? "viewer"` fallback locks owner-only
  // controls (the autonomy master switch, workspace settings) until a full
  // workspace-list re-sync on the next page refresh.
  const created: Workspace = {
    id: ws.id,
    name: ws.name,
    ownerId: ws.ownerId ?? "",
    avatarUrl: null,
    role: "owner",
    createdAt: ws.createdAt ?? Date.now(),
    settings: ws.settings ?? {},
  };
  // By id: a keyed retry answers with a workspace this list may already hold (a reload
  // re-read the list after the first create landed), and it must not be listed twice.
  workspaceState.list = [...workspaceState.list.filter((w) => w.id !== created.id), created];
  return created;
}

// ─── Projects warm cache (fix: prevent flash-of-blank on swipe-back) ────────
//
// Projects/channelProjectMap used to live in ChannelSidebar component-local
// $state and was re-fetched from the network on EVERY mount.  On mobile the
// Home (/home) and a conversation are separate routes, so
// swipe-back unmounts the sidebar.  On remount projects=[] until the async
// fetch resolves (~200-400 ms) — causing blank/incomplete project headers
// because channels are WARM (in global workspaceState) but projects were cold.
//
// Fix: a module-level Map keyed by wsId.  On first fetch the result is written
// here; subsequent sidebar mounts read it synchronously so projects paint on
// the FIRST frame.  A background refresh fires on every mount and swaps in
// fresh data when it lands — no visible gap.  Mutations (create/edit/delete)
// write back immediately so a later remount reflects them.  On workspace switch
// the stale workspace's entry is cleared; on logout all entries are wiped.

export interface ProjectsCacheEntry {
  // createdAt is optional to match the component-local Project interface in ChannelSidebar.
  projects: { id: string; name: string; color: string; createdAt?: number; keyPrefix?: string }[];
  channelProjectMap: Map<string, string>;
}

// Value equality, and only between DIFFERENT instances: a caller that mutated the
// stored arrays in place and re-set them must still notify readers.
function sameProjectsEntry(a: ProjectsCacheEntry, b: ProjectsCacheEntry): boolean {
  if (a.projects === b.projects || a.channelProjectMap === b.channelProjectMap) return false;
  if (a.projects.length !== b.projects.length) return false;
  if (a.channelProjectMap.size !== b.channelProjectMap.size) return false;
  for (let i = 0; i < a.projects.length; i++) {
    const p = a.projects[i];
    const q = b.projects[i];
    if (!p || !q) return false;
    if (
      p.id !== q.id ||
      p.name !== q.name ||
      p.color !== q.color ||
      p.createdAt !== q.createdAt ||
      p.keyPrefix !== q.keyPrefix
    ) {
      return false;
    }
  }
  for (const [channelId, projectId] of a.channelProjectMap) {
    if (b.channelProjectMap.get(channelId) !== projectId) return false;
  }
  return true;
}

class ProjectsWarmCache {
  private _cache = $state<Map<string, ProjectsCacheEntry>>(new Map());

  get(wsId: string): ProjectsCacheEntry | undefined {
    return this._cache.get(wsId);
  }

  set(wsId: string, entry: ProjectsCacheEntry): void {
    // An unchanged answer keeps the stored entry: a new Map re-ran every effect that
    // reads the cache, and those effects re-fetch while the list is empty (CYBORG-1204).
    const prev = this._cache.get(wsId);
    if (prev && sameProjectsEntry(prev, entry)) return;
    const next = new Map(this._cache);
    next.set(wsId, entry);
    this._cache = next;
  }

  delete(wsId: string): void {
    if (!this._cache.has(wsId)) return;
    const next = new Map(this._cache);
    next.delete(wsId);
    this._cache = next;
  }

  clear(): void {
    this._cache = new Map();
  }
}

export const projectsCache = new ProjectsWarmCache();

// The server renames the default department along with the workspace (renameDefaultProject:
// every department named exactly like the old name). Mirror that in the warm cache, so a
// sidebar mounted right after a rename still finds the default department by its new name
// instead of drawing it as an ordinary row until the next fetch lands.
export function renameDefaultProjectInCache(wsId: string, oldName: string, newName: string): void {
  const entry = projectsCache.get(wsId);
  if (!entry || oldName === newName || !entry.projects.some((p) => p.name === oldName)) return;
  projectsCache.set(wsId, {
    ...entry,
    // Copies, not in place: the sidebar holds these objects as its own state.
    projects: entry.projects.map((p) =>
      p.name === oldName ? Object.assign({}, p, { name: newName }) : p,
    ),
  });
}

// `taskChannelId`/`workspaceId`: a task is about to be created from a message there.
export async function fetchProjects(
  opts: { taskChannelId?: string; workspaceId?: string } = {},
): Promise<{
  projects: { id: string; name: string; color: string; createdAt: number; keyPrefix?: string }[];
  channelProjects: { channelId: string; projectId: string }[];
  defaultProjectId?: string | null; // null: they can file nowhere; absent: the create seeds one
}> {
  const wsId = opts.workspaceId ?? workspaceState.current?.id;
  if (!wsId) return { projects: [], channelProjects: [] };
  const { taskChannelId } = opts;
  const result = await (taskChannelId ? client.fetchProjects(wsId, taskChannelId) : client.fetchProjects(wsId)); // prettier-ignore
  // Populate the warm cache so subsequent sidebar mounts paint instantly.
  projectsCache.set(wsId, {
    projects: result.projects,
    channelProjectMap: new Map(result.channelProjects.map((cp) => [cp.channelId, cp.projectId])),
  });
  return result;
}

export async function createProject(
  name: string,
  color: string,
  idempotencyKey?: string,
): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const project = await client.createProject(
    workspaceState.current.id,
    name,
    color,
    idempotencyKey,
  );
  return project.id;
}

export async function updateProject(projectId: string, name: string, color: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.updateProject(workspaceState.current.id, projectId, name, color);
}

export async function deleteProject(projectId: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.deleteProject(workspaceState.current.id, projectId);
}

export async function setChannelProject(
  channelId: string,
  projectId: string | null,
): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId) throw new Error("no active workspace");
  await client.setChannelProject(wsId, channelId, projectId);
}

// ─── Channel management ─────────────────────────────────────────

export async function createChannel(
  name: string,
  opts?: {
    description?: string;
    isPrivate?: boolean;
    instructions?: string;
    projectId?: string | null;
    // CYBORG-1082: one key per create-form session; the relay answers a repeat with the
    // channel that key already made instead of writing a second one.
    idempotencyKey?: string;
  },
): Promise<import("../core/types.js").Channel | null> {
  if (!workspaceState.current) return null;
  // Spread, not a field list: the hand-copied list is what dropped idempotencyKey on the way
  // to the relay (CYBORG-1082). The server writes the project link atomically with the
  // channel; the retry loop below stays as a fallback for an older relay that ignores it.
  const ch = await client.createChannel(workspaceState.current.id, name, { ...opts });
  const channel = { ...ch, projectId: null as string | null };
  if (!workspaceState.channels.some((c) => c.id === channel.id)) {
    workspaceState.channels = [...workspaceState.channels, channel];
  }
  if (opts?.projectId) {
    // Persist the channel→project link. This used to be fire-and-forget with the
    // failure swallowed by an empty arrow catch, so a transient failure dropped
    // the project and the channel reverted to "no project" on reload. Retry with
    // backoff and SURFACE a real failure instead of hiding it.
    const projectId = opts.projectId;
    let saved = false;
    for (let attempt = 0; attempt < 3 && !saved; attempt += 1) {
      try {
        await client.setChannelProject(channel.workspaceId, channel.id, projectId);
        saved = true;
      } catch (err) {
        if (attempt === 2) {
          console.warn("createChannel: failed to assign project after retries", err);
        } else {
          await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
        }
      }
    }
    // Only reflect the project AFTER the server accepted it — never show a state
    // the server rejected (avoids the under-project → Unfiled flicker on a failed
    // save). Per Gemini review: update post-await, not optimistically.
    if (saved) {
      channel.projectId = projectId;
      // Update the channel IN the array by id: if a concurrent channel_created
      // broadcast already inserted a DIFFERENT object, mutating our local ref
      // wouldn't reach the sidebar. (Gemini review.)
      workspaceState.channels = workspaceState.channels.map((c) =>
        c.id === channel.id ? { ...c, projectId } : c,
      );
    }
  }
  return channel;
}

// #608: start a group DM with the given OTHER members (the creator is implicit).
// The server validates membership + the 2–8 cap and derives the auto-name; the
// returned hidden group_dm channel is added to our sidebar immediately (the
// broadcast adds it for the other members). Returns the new channel, or null if
// there is no active workspace.
export async function createGroupDm(
  participants: string[],
): Promise<import("../core/types.js").Channel | null> {
  if (!workspaceState.current) return null;
  const ch = await client.createGroupDm(workspaceState.current.id, participants);
  if (!workspaceState.channels.some((c) => c.id === ch.id)) {
    workspaceState.channels = [...workspaceState.channels, ch];
  }
  return ch;
}

export async function updateChannel(
  channelId: string,
  updates: {
    name?: string;
    description?: string | null;
    isPrivate?: boolean;
    instructions?: string | null;
    // The channel's autonomy ceiling (Cybo Autonomy #964) — the room's own cap
    // on any agent's effective level here. null explicitly clears it back to
    // "unset" (resolveEffectiveAutonomy's behavior-neutral L4 default); omit
    // the key to leave the stored value untouched.
    maxAutonomyLevel?: AutonomyLevel | null;
  },
): Promise<void> {
  if (!workspaceState.current) return;
  const ch = await client.updateChannel(workspaceState.current.id, channelId, updates);
  // null means the save landed but this person may not read the channel back (a private
  // channel they are not in). Nothing to merge, and it is NOT an error to report.
  if (!ch) return;
  workspaceState.channels = workspaceState.channels.map((c) => (c.id === ch.id ? ch : c));
}

export async function deleteChannel(channelId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.deleteChannel(workspaceState.current.id, channelId);
  workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channelId);
  if (channelState.activeId === channelId) {
    channelState.activeId = null;
  }
}

// Archive (soft-delete) vs deleteChannel: preserves the channel + history but
// hides it from the active list. Mirrors deleteChannel's local reconciliation
// when archiving (drop from sidebar / clear active selection); unarchive re-adds
// it from the returned channel.
export async function archiveChannel(channelId: string, archived: boolean): Promise<void> {
  if (!workspaceState.current) return;
  const ch = await client.archiveChannel(workspaceState.current.id, channelId, archived);
  if (archived) {
    workspaceState.channels = workspaceState.channels.filter((c) => c.id !== channelId);
    notificationState.clear(workspaceState.current.id, channelId);
    unreadFlagState.clearUnread(workspaceState.current.id, channelId);
    if (channelState.activeId === channelId) {
      channelState.activeId = null;
    }
  } else if (ch) {
    // Unarchived: re-insert (or update) the channel in the active list.
    const exists = workspaceState.channels.some((c) => c.id === ch.id);
    workspaceState.channels = exists
      ? workspaceState.channels.map((c) => (c.id === ch.id ? ch : c))
      : [...workspaceState.channels, ch];
  }
}

// Caller's role in a channel from the real channel_roles table ("admin" |
// "member" | null). Replaces the createdBy === myId admin heuristic.
export async function getChannelRole(channelId: string): Promise<string | null> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return null;
  return client.getChannelRole(wsId, channelId);
}

// Lightweight file count for the Files tab badge. Fail-soft: returns 0 if the
// workspace context is missing (the badge just shows nothing).
export async function getChannelFileCount(channelId: string): Promise<number> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return 0;
  return client.getChannelFileCount(wsId, channelId);
}

export async function fetchChannelMembers(
  channelId: string,
): Promise<import("../core/types.js").ChannelMember[]> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return [];
  const { members, slackGuests } = await client.fetchChannelMembersWithSlackGuests(wsId, channelId);
  rememberChannelSlackGuests(channelId, slackGuests);
  return members;
}

export async function addChannelMember(channelId: string, userId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.addChannelMember(workspaceState.current.id, channelId, userId);
}

export async function removeChannelMember(channelId: string, userId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.removeChannelMember(workspaceState.current.id, channelId, userId);
}

// ── Cybo channel membership (W3) ──
export async function fetchChannelCybos(channelId: string): Promise<string[]> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return [];
  return client.fetchChannelCybos(wsId, channelId);
}

// Same read, keeping the display identity beside the ids — see the client method's
// own comment for why a Cyborg7 Connect external has no other source for it.
export async function fetchChannelCybosWithIdentity(channelId: string): Promise<{
  cyboIds: string[];
  cybos: { id: string; slug: string; name: string; avatar: string | null }[];
}> {
  const wsId = workspaceState.channelWorkspaceId(channelId);
  if (!wsId) return { cyboIds: [], cybos: [] };
  const { humans, ...agents } = await client.fetchChannelCybosWithIdentity(wsId, channelId);
  rememberChannelSeatedHumans(channelId, humans); // the mention pills + picker of THIS channel
  return agents;
}
setChannelSeatedHumansLoader(fetchChannelCybosWithIdentity);

export async function addChannelCybo(channelId: string, cyboId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.addChannelCybo(workspaceState.current.id, channelId, cyboId);
}

export async function removeChannelCybo(channelId: string, cyboId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.removeChannelCybo(workspaceState.current.id, channelId, cyboId);
}

// ── Project cybo roles + daily brief (knowledge/82) ──
export async function fetchProjectRoles(
  projectId: string,
): Promise<import("../ws-client.js").ProjectCyboRole[]> {
  if (!workspaceState.current) return [];
  return client.fetchProjectRoles(workspaceState.current.id, projectId);
}

export async function setProjectRole(
  projectId: string,
  cyboId: string,
  role: string,
  instructions?: string | null,
): Promise<void> {
  if (!workspaceState.current) return;
  await client.setProjectRole(workspaceState.current.id, projectId, cyboId, role, instructions);
}

export async function setProjectPm(projectId: string, cyboId: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.setProjectPm(workspaceState.current.id, projectId, cyboId);
}

export async function fetchProjectBrief(projectId: string): Promise<string | null> {
  if (!workspaceState.current) return null;
  return client.fetchProjectBrief(workspaceState.current.id, projectId);
}

export async function setProjectBrief(projectId: string, dailyBrief: string): Promise<void> {
  if (!workspaceState.current) return;
  await client.setProjectBrief(workspaceState.current.id, projectId, dailyBrief);
}

// ── Project autonomy LOOP config (Phase A, knowledge/82) ──
export async function fetchProjectAutonomy(
  projectId: string,
): Promise<import("../ws-client.js").ProjectAutonomySettings | null> {
  if (!workspaceState.current) return null;
  return client.fetchProjectAutonomy(workspaceState.current.id, projectId);
}

export async function setProjectAutonomy(
  projectId: string,
  patch: Partial<
    Pick<
      import("../ws-client.js").ProjectAutonomySettings,
      "loopEnabled" | "cadence" | "monthlyBudgetUsd" | "maxTurnsPerRun"
    >
  >,
): Promise<void> {
  if (!workspaceState.current) return;
  await client.setProjectAutonomy(workspaceState.current.id, projectId, patch);
}

// ── Agent-channel picker (knowledge/82's swarm-regime coordination channel) ──
export async function setProjectAgentChannel(
  projectId: string,
  channelId: string | null,
): Promise<void> {
  if (!workspaceState.current) return;
  await client.setProjectAgentChannel(workspaceState.current.id, projectId, channelId);
}

export async function createProjectAgentChannel(projectId: string): Promise<string> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.createProjectAgentChannel(workspaceState.current.id, projectId);
}

// ── PM run ledger (knowledge/82's per-project progress ledger) ──
export async function fetchProjectActivity(
  projectId: string,
  limit?: number,
): Promise<import("../ws-client.js").PmRunLedgerEvent[]> {
  if (!workspaceState.current) return [];
  return client.fetchProjectActivity(workspaceState.current.id, projectId, limit);
}
// ─── Shared Files (M-files) ─────────────────────────────────────
// Cursor pagination (newest-first). Fail-soft: the panel shows an inline error
// rather than throwing into the UI tree.

export async function fetchChannelFiles(
  channelId: string,
  opts?: { before?: string; limit?: number },
): Promise<import("../ws-client.js").SharedFilesPage> {
  if (!workspaceState.current) return { files: [], hasMore: false, nextCursor: null };
  return client.fetchChannelFiles(workspaceState.current.id, channelId, opts);
}

export async function fetchDmFiles(
  peerId: string,
  opts?: { before?: string; limit?: number },
): Promise<import("../ws-client.js").SharedFilesPage> {
  if (!workspaceState.current) return { files: [], hasMore: false, nextCursor: null };
  return client.fetchDmFiles(workspaceState.current.id, peerId, opts);
}

// All workspace channels for browse/search (public + your private), with isMember.
export async function listChannels(): Promise<
  Array<Channel & { isMember: boolean; memberCount: number }>
> {
  if (!workspaceState.current) return [];
  return client.listChannels(workspaceState.current.id);
}

// Full-text message search in the current workspace (Postgres FTS server-side).
// Rejects on failure — a failed search and an empty one must not be the same answer.
// The [] below is only ever "no workspace yet" or "no query yet"; callers already
// guard on a workspace, so it never reaches a user as "no results".
export async function searchMessages(
  query: string,
  scope?: import("../core/client.js").MessageSearchScope,
): Promise<import("../core/types.js").Message[]> {
  const wsId = workspaceState.current?.id;
  if (!wsId || (!query.trim() && !scope)) return [];
  return client.searchMessages(wsId, query, 50, scope);
}

// Workspace-wide task search (title OR description, ILIKE server-side).
// Rejects on failure — a failed search and an empty one must not be the same answer.
// The [] below is only ever "no workspace yet" or "query under 2 characters"; callers
// already guard on a workspace, so it never reaches a user as "no results".
export async function searchTasks(
  query: string,
): Promise<import("../core/types.js").TaskSearchResult[]> {
  const wsId = workspaceState.current?.id;
  if (!wsId || query.trim().length < 2) return [];
  return client.searchTasks(wsId, query);
}

// Workspace-wide page search for the top search bar: title OR body, with the card fields.
// Rejects on failure, like searchTasks; [] only for no workspace / a query under 2 characters.
export async function searchPages(
  query: string,
): Promise<import("../core/types.js").PageSearchResult[]> {
  const wsId = workspaceState.current?.id;
  if (!wsId || query.trim().length < 2) return [];
  return (await client.searchPages(wsId, query, { mode: "detail", limit: 20 })).pages;
}

// Self-join a channel: add to channel_members, surface it in the sidebar.
export async function joinChannel(channel: Channel | { id: string }): Promise<void> {
  if (!workspaceState.current || !authState.user) return;
  const channelId = channel.id;
  await client.addChannelMember(workspaceState.current.id, channelId, authState.user.id);
  if (!workspaceState.channels.some((c) => c.id === channelId) && "name" in channel) {
    workspaceState.channels = [...workspaceState.channels, channel as Channel];
  }
  // Preview → joined transition: once the channel is in `channels`, `activeChannel`
  // resolves and `isPreviewing` flips false, so the preview banner swaps to the
  // composer with no reload. Clear the preview slot for the channel we just joined.
  if (workspaceState.previewChannel?.id === channelId) {
    workspaceState.previewChannel = null;
  }
}
